import { useQuery } from "@tanstack/react-query";

export interface MediaCategory {
  id: number;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  articleCount: number;
}

export function formatCategorySlug(slug: string): string {
  return slug
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function useMediaCategories(options?: { includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  const queryParam = includeInactive ? "?includeInactive=true" : "";

  const { data, isLoading } = useQuery<MediaCategory[]>({
    queryKey: ["/api/media-categories", { includeInactive }],
    queryFn: async () => {
      const res = await fetch(`/api/media-categories${queryParam}`);
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const categories = data ?? [];

  const categoryMap: Record<string, string> = {};
  for (const cat of categories) {
    categoryMap[cat.slug] = cat.name;
  }

  const getCategoryLabel = (slug: string): string => {
    return categoryMap[slug] || formatCategorySlug(slug);
  };

  return {
    categories,
    categoryMap,
    getCategoryLabel,
    isLoading,
  };
}
